import React, { useEffect, useState } from 'react';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import Text from '../components/Text';
import FluidImage from '../components/FluidImage';
import Link from '../components/Link';
import PropTypes from 'prop-types';
import { GTMEvent } from '../utils/gtm';
import ReactPlayer from 'react-player';
import { getKeys } from '@src/utils/dictionary';
import Module from '../components/Module';
import { Container, Chip, Box } from '@material-ui/core';
import theme from '../site/theme';
import Button from '../components/Button';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    top: 0,
    marginTop: 80,
  },
  container: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: '32px !important',
      paddingRight: '32px !important',
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '16px !important',
      paddingRight: '16px !important',
    },
  },
  productWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    rowGap: 48,
    columnGap: 32,
    boxSizing: 'border-box',
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      rowGap: 10,
      columnGap: 0,
    },
  },
  cardContainer: {
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    height: 'auto',
    [theme.breakpoints.down('sm')]: {
      transition: 'none',
    },
    '&:hover': {
      transform: 'translateY(-16px)',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
      [theme.breakpoints.down('sm')]: {
        transform: 'none',
        boxShadow: 'unset',
      },
    },
  },
  cardContainerSize50: {
    flex: '1 1 calc(50% - 16px)',
    maxWidth: 'calc(50% - 16px)',
    width: 'calc(50% - 16px)',
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 100%',
      width: '100%',
      maxWidth: '100%',
    },
  },
  cardContainerSize33: {
    flex: '0 0 calc(33.333% - 21.33px)',
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 100%',
    },
  },
  cardContainerSize100: {
    flex: '0 0 100%',
    height: '100%',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  cardSize100: {
    display: 'flex',
    position: 'relative',
    maxHeight: '450px',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  card100foregroundImg: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
    },
  },
  card100foregroundImgWrapper: {
    height: '100%',
  },
  card100TextContainer: {
    position: 'relative',
    zIndex: 2,
    width: '37%',
    display: 'flex',
    marginLeft: '56px',
    marginRight: '32px',
  },
  card100MediaContainer: {
    width: '63%',
    height: '100%',
    position: 'relative',
  },
  card100imageWrapper: {
    height: '100%',
    width: '100%',
    aspectRatio: '16/9',
  },
  productListManualContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '32px',
    height: 'fit-content',
    alignSelf: 'end',
    [theme.breakpoints.down('md')]: {
      marginBottom: '24px',
    },
  },
  pretitle100: {
    fontFamily: 'Spezia Web',
    fontSize: '18px',
    lineHeight: '26px',
    color: '#FFFFFF',
  },
  title100: {
    fontSize: '2rem',
    fontFamily: 'Druk Wide Web',
    fontWeight: 'bold',
    lineHeight: '1.9rem',
    color: '#FFFFFF',
    marginBottom: 16,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.25rem',
    },
  },
  card100bgWrapper: {
    height: '100%',
    width: '100%',
    '& img': {
      objectFit: 'cover',
      objectPosition: 'center',
      width: '100%',
      height: '100%',
    },
  },
  card100BgContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  card: {
    position: 'relative',
    backgroundColor: '#fff',
    width: '100%',
    height: '100%',
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  cardLink: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 100,
    background: 'transparent',
  },
  cardHeader: {
    position: 'relative',
    aspectRatio: '16 / 9',
    height: 'fit-content',
  },
  cardBody: {
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      padding: 16,
    },
  },
  imageWrapper: {
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  videoWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transition: 'opacity 0.8s ease-in-out',
    opacity: 1,
    zIndex: 99,
  },
  hidden: {
    opacity: 0,
  },
  foregroundImg: {
    height: '120%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
    },
  },
  imgContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    '& img': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      objectPosition: 'center',
    },
    [theme.breakpoints.up('md')]: {
      paddingTop: 'unset',
    },
  },
  legalText: {
    position: 'absolute',
    left: 40,
    right: 40,
    bottom: 10,
    fontSize: '14px',
    textAlign: 'center',
    fontFamily: 'Spezia',
    zIndex: 99,
    [theme.breakpoints.down('md')]: {
      fontSize: '10px',
      left: 10,
      right: 10,
    },
  },
  mainTitle: {
    fontFamily: 'Spezia',
    fontWeight: 700,
    fontSize: 35.2,
    lineHeight: '120%',
    textAlign: 'center',
    color: '#000000',
  },
  cardWrapper: {
    height: '100%',
    padding: 0,
  },
  badge: {
    position: 'absolute',
    top: 24,
    right: 0,
    height: 28,
    background: '#FF2765',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 98,
    padding: '2px 8px',
    boxSizing: 'border-box',
  },
  badgeTitle: {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: 16,
    color: '#FFFFFF',
    textTransform: 'uppercase'
  },
  categoriesProductListBackground: {
    backgroundColor: '#f6f6f6',
    width: '100%',
    height: 'calc(100% - 200px)',
    position: 'absolute',
    zIndex: -1,
    marginTop: 200,
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'transparent',
    },
  },
  categoriesProductListCtaContainer: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
    padding: '16px 0px 40px 0px',
    fontFamily: '"Poppins"',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '24px',
    color: '#005c69',
    [theme.breakpoints.down('md')]: {
      padding: '0px 0px 40px 0px',
    },
  },
  logo: {
    height: 30,
    position: 'absolute',
    top: 24,
    left: 24,
    zIndex: 3,
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
    },
    [theme.breakpoints.up('md')]: {
      height: 100,
    },
  },
  logoNarrowDesktop: {
    [theme.breakpoints.up('sm')]: {
      height: '160px !important',
      marginTop: 0,
    },
    '& img': {
      width: 'auto',
      height: '100%',
      objectFit: 'contain',
    },
  },
  logoNarrowMobile: {
    height: '80px !important',
    marginTop: 0,
    [theme.breakpoints.down('md')]: {
      height: '40px !important',
    },
    '& img': {
      width: 'auto',
      height: '100%',
      objectFit: 'contain',
    },
  },
  positionCenterLogo: {
    justifyContent: 'center',
    display: 'flex',
  },
  preorder: {
    fontFamily: 'Spezia',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '21px',
    color: '#9D75B1',
    margin: 0,
  },
  title: {
    fontFamily: 'Druk Wide Web',
    fontWeight: 700,
    fontSize: 24,
    lineHeight: '140%',
    color: '#581D70',
    marginTop: 8,
    flexGrow: 1,
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: {
      color: '#390B45',
    },
  },
  button: {
    paddingLeft: 42,
    paddingRight: 42,
    [theme.breakpoints.down('sm')]: {
      width: 'fit-content',
    },
  },
  ctaButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 16,
    },

  },
  textPrice: {
    fontFamily: 'Spezia',
    fontSize: 16,
    color: '#390B45'
  }
}));

const fallbackProperty = product => {
  let fallbackProperty = new Object();
  const pModule = product?.field_value;
  const pCatalog = pModule?.product?.field_value;

  fallbackProperty.box_size = pModule?.box_size?.field_value || '100';
  fallbackProperty.cta = pModule?.cta?.field_value?.href
    ? pModule?.cta
    : pCatalog?.list_box_v2_cta;
  fallbackProperty.image_foreground = pModule?.image_foreground?.field_value
    ? pModule?.image_foreground
    : pCatalog?.list_box_v2_image_foreground;
  fallbackProperty.image_foreground_50 = pModule?.image_foreground_50?.field_value
    ? pModule?.image_foreground_50
    : pCatalog?.list_box_v2_image_foreground_50;
  fallbackProperty.image_foreground_100 = pModule?.image_foreground_100?.field_value
    ? pModule?.image_foreground_100
    : pCatalog?.list_box_v2_image_foreground_100;
  fallbackProperty.image_full_width = pModule?.image_full_width?.field_value
    ? pModule?.image_full_width
    : pCatalog?.list_box_v2_image_full_width;
  fallbackProperty.image_half_width = pModule?.image_half_width?.field_value
    ? pModule?.image_half_width
    : pCatalog?.list_box_v2_image_half_width;
  fallbackProperty.image_mobile = pModule?.image_mobile?.field_value
    ? pModule?.image_mobile
    : pCatalog?.list_box_v2_image_mobile;
  fallbackProperty.narrow_logo_desktop = pModule?.narrow_logo_desktop?.field_value
    ? pModule?.narrow_logo_desktop
    : pCatalog?.list_box_v2_narrow_logo_desktop;
  fallbackProperty.narrow_logo_mobile = pModule?.narrow_logo_mobile?.field_value
    ? pModule?.narrow_logo_mobile
    : pCatalog?.list_box_v2_narrow_logo_mobile;
  fallbackProperty.position_center_logo = pModule?.position_center_logo?.field_value
    ? pModule?.position_center_logo
    : pCatalog?.list_box_v2_position_center_logo;
  fallbackProperty.pre_order = pModule?.pre_order?.field_value
    ? pModule?.pre_order
    : pCatalog?.list_box_v2_pre_order;
  fallbackProperty.up_title = pModule?.up_title?.field_value
    ? pModule?.up_title
    : pCatalog?.list_box_v2_up_title;
  fallbackProperty.discover_now = pModule?.discover_now?.field_value
    ? pModule?.discover_now
    : pCatalog?.list_box_v2_discover_now;
  fallbackProperty.title = pCatalog?.list_box_v2_title?.field_value
    ? pCatalog?.list_box_v2_title
    : '';
  fallbackProperty.pre_title = pModule['pre-title']?.field_value
    ? pModule['pre-title']
    : pCatalog?.list_box_v2_pre_title;
  fallbackProperty.logo = pModule?.product?.field_value?.list_box_v2_logo_card_100?.field_value
    ? pModule?.product?.field_value?.list_box_v2_logo_card_100
    : pCatalog?.menu_logo;
  fallbackProperty.productPreOrder = pModule?.cta?.field_value?.isPreorder || false;
  fallbackProperty.video = pCatalog?.list_box_v2_video_url?.field_value;
  fallbackProperty.legal_price = pModule?.legal_price?.field_value && pModule?.legal_price;
  fallbackProperty.linecolor = pModule?.linecolor?.field_value && pModule?.linecolor;
  fallbackProperty.enabledVideo = pCatalog?.list_box_v2_enable_video_url?.field_value || false;

  return fallbackProperty;
};

const renderButton = (classes, productFallbackProperty, repo, isCard100 = false) => {
  const cta = productFallbackProperty?.cta?.field_value;
  const discoverNow = productFallbackProperty?.discover_now?.field_value;
  const label = cta?.label || discoverNow;

  if (!cta && !discoverNow) return null;

  const buttonProps = {
    className: classes.button,
    ...cta,
    variant: 'contained',
    next: true,
    size: 'large',
    trackingObj: repo,
    trackingLabel: `CategoryProductListV3 - ${discoverNow || cta?.label || cta?.title || cta?.href}`,
  };

  return isCard100 ? (
    <Button {...buttonProps}>{label}</Button>
  ) : discoverNow ? (
    <div className={classes.ctaButtonWrapper}>
      <Text className={classes.textPrice}>{discoverNow}</Text>
      <Button {...buttonProps}>{label}</Button>
    </div>
  ) : (
    <Button {...buttonProps}>{label}</Button>
  );
};

const Card100 = ({product, index, info, isMobile}) => {
  const classes = useStyles();
  const productFallbackProperty = fallbackProperty(product);
  const [wrapper, setWrapper] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const isVideoEnabled = productFallbackProperty?.enabledVideo && productFallbackProperty?.video;

  useEffect(() => {
    async function fetchData() {
      setWrapper(await getKeys({labelPreOrder: 'pre-order.label'}));
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (isHovering) {
      setIsVideoPlaying(true);
    } else {
      const timer = setTimeout(() => setIsVideoPlaying(false), 1300);
      return () => clearTimeout(timer);
    }
  }, [isHovering]);

  return (
    <div
      className={`${classes.cardContainer} ${classes.cardContainerSize100}`}
    >
      <Module className={classes.cardWrapper} key={index}>
        <div
          className={classes.cardSize100}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <Link
            {...productFallbackProperty?.cta?.field_value}
            className={classes.cardLink}
            aria-label={`View ${productFallbackProperty?.title?.field_value || 'product'}`}
          />
          <div className={classes.card100TextContainer}>
            {productFallbackProperty?.logo?.field_value && (
              <div
                className={`${classes.logo} ${
                  productFallbackProperty?.narrow_logo_desktop?.field_value
                    ? classes.logoNarrowDesktop
                    : ''
                } ${
                  productFallbackProperty?.narrow_logo_mobile?.field_value
                    ? classes.logoNarrowMobile
                    : ''
                } ${
                  productFallbackProperty?.position_center_logo?.field_value
                    ? classes.positionCenterLogo
                    : ''
                }`}
              >
                <FluidImage {...productFallbackProperty?.logo?.field_value} />
              </div>
            )}
            <div className={classes.productListManualContent}>
              {productFallbackProperty.productPreOrder ? (
                <Box>
                  <Chip label={wrapper.labelPreOrder}/>
                </Box>
              ) : (
                productFallbackProperty?.pre_title?.field_value && (
                  <Text
                    className={classes.pretitle100}
                    dangerouslySetInnerHTML={{
                      __html: productFallbackProperty?.pre_title?.field_value.replace(/<\/?[^>]+(>|$)/g, ''),
                    }}
                  />
                )
              )}
              <Text className={classes.title100}>{productFallbackProperty?.title?.field_value}</Text>
              {renderButton(classes, productFallbackProperty, info, true)}
            </div>
          </div>
          <div className={classes.card100MediaContainer}>
            <div
              className={`${classes.card100imageWrapper} ${
                isHovering && isVideoEnabled && !isMobile ? classes.hidden : ''
              }`}
            >
              {productFallbackProperty?.image_foreground_100?.field_value && (
                <div className={classes.card100foregroundImg}>
                  <FluidImage
                    className={classes.card100foregroundImgWrapper} {...productFallbackProperty?.image_foreground_100?.field_value} />
                </div>
              )}
              {productFallbackProperty?.image_full_width?.field_value && (
                <FluidImage {...productFallbackProperty?.image_full_width?.field_value} />
              )}
            </div>
            {isVideoEnabled && (
              <div
                className={`${classes.videoWrapper} ${
                  !isHovering || isMobile ? classes.hidden : ''
                }`}
              >
                <ReactPlayer
                  url={productFallbackProperty?.video}
                  playing={isVideoPlaying}
                  muted
                  loop
                  width="100%"
                  height="100%"
                  style={{position: 'absolute', top: 0, left: 0, objectFit: 'contain'}}
                  config={{
                    youtube: {
                      playerVars: {
                        controls: 0,
                        showinfo: 0,
                        rel: 0,
                        fs: 0,
                        iv_load_policy: 3,
                        modestbranding: 1,
                        loop: 1,
                        autoplay: 1,
                      },
                    },
                  }}
                />
              </div>
            )}
          </div>
          <div className={classes.card100BgContainer}>
            {productFallbackProperty?.up_title?.field_value && (
              <div className={classes.badge}>
                <Text
                  className={classes.badgeTitle}
                  dangerouslySetInnerHTML={{__html: productFallbackProperty?.up_title?.field_value}}
                />
              </div>
            )}
            {productFallbackProperty?.image_full_width?.field_value && (
              <FluidImage
                className={classes.card100bgWrapper}
                {...productFallbackProperty?.image_full_width?.field_value}
              />
            )}
          </div>
        </div>
      </Module>
    </div>
  );
};

Card100.propTypes = {
  product: PropTypes.object,
  index: PropTypes.number,
  info: PropTypes.object,
  isMobile: PropTypes.bool,
};

const Card50 = ({product, size, index, info, isMobile}) => {
  const classes = useStyles();
  const productFallbackProperty = fallbackProperty(product);
  const [wrapper, setWrapper] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const isVideoEnabled = productFallbackProperty?.enabledVideo && productFallbackProperty?.video;

  useEffect(() => {
    async function fetchData() {
      setWrapper(await getKeys({labelPreOrder: 'pre-order.label'}));
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (isHovering) {
      setIsVideoPlaying(true);
    } else {
      const timer = setTimeout(() => setIsVideoPlaying(false), 1300);
      return () => clearTimeout(timer);
    }
  }, [isHovering]);

  return (
    size && (
      <div
        className={`${classes.cardContainer} ${classes[`cardContainerSize${size}`]}`}
      >
        <Module className={classes.cardWrapper} key={index}>
          <div
            className={classes.card}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <Link
              {...productFallbackProperty?.cta?.field_value}
              className={classes.cardLink}
              aria-label={`View ${productFallbackProperty?.title?.field_value || 'product'}`}
            />
            <div className={classes.cardHeader}>
              {productFallbackProperty?.up_title?.field_value && (
                <div className={classes.badge}>
                  <Text
                    className={classes.badgeTitle}
                    dangerouslySetInnerHTML={{__html: productFallbackProperty?.up_title?.field_value}}
                  />
                </div>
              )}
              <div
                className={`${classes.imageWrapper} ${
                  isHovering && isVideoEnabled && !isMobile ? classes.hidden : ''
                }`}
              >
                {(size === '50'
                  ? productFallbackProperty?.image_foreground_50?.field_value
                  : productFallbackProperty?.image_foreground?.field_value) && (
                  <FluidImage
                    className={classes.foregroundImg}
                    {...(size === '50'
                      ? productFallbackProperty?.image_foreground_50?.field_value
                      : productFallbackProperty?.image_foreground?.field_value)}
                  />
                )}
                {productFallbackProperty?.image_half_width?.field_value && (
                  <FluidImage
                    className={classes.imgContainer}
                    {...productFallbackProperty?.image_half_width?.field_value}
                  />
                )}
              </div>
              {productFallbackProperty?.legal_price?.field_value && size === '50' && (
                <div
                  className={classes.legalText}
                  style={{color: productFallbackProperty?.linecolor?.field_value}}
                >
                  {productFallbackProperty?.legal_price?.field_value}
                </div>
              )}
              {isVideoEnabled && (
                <div
                  className={`${classes.videoWrapper} ${
                    !isHovering || isMobile ? classes.hidden : ''}`}
                >
                  <ReactPlayer
                    url={productFallbackProperty?.video}
                    playing={isVideoPlaying}
                    muted
                    loop
                    width="100%"
                    height="100%"
                    style={{position: 'absolute', top: 0, left: 0, objectFit: 'contain'}}
                    config={{
                      youtube: {
                        playerVars: {
                          controls: 0,
                          showinfo: 0,
                          rel: 0,
                          fs: 0,
                          iv_load_policy: 3,
                          modestbranding: 1,
                          loop: 1,
                          autoplay: 1,
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>
            <div className={classes.cardBody}>
              {productFallbackProperty.productPreOrder ? (
                <Text
                  className={classes.preorder}
                  dangerouslySetInnerHTML={{__html: wrapper.labelPreOrder}}
                />
              ) : (
                productFallbackProperty?.pre_title?.field_value && (
                  <Text
                    className={classes.preorder}
                    dangerouslySetInnerHTML={{__html: productFallbackProperty?.pre_title?.field_value}}
                  />
                )
              )}
              <Text className={classes.title}>{productFallbackProperty?.title?.field_value}</Text>
              {renderButton(classes, productFallbackProperty, info, false)}
            </div>
          </div>
        </Module>
      </div>
    )
  );
};

Card50.propTypes = {
  product: PropTypes.object,
  index: PropTypes.number,
  size: PropTypes.string,
  info: PropTypes.object,
  isMobile: PropTypes.bool,
};

const CategoriesProductListManualV2Module = ({products, title, info, see_all_new_arrivals}) => {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <div className={classes.root}>
      <div className={classes.categoriesProductListBackground}/>
      <Container className={classes.container}>
        <Module>
          <Text
            variant="titleXXXL"
            align="center"
            weight="bold"
            overline
            gutterBottom
            className={classes.mainTitle}
          >
            {title?.field_value}
          </Text>
        </Module>
        <div className={classes.productWrapper}>
          {products?.field_value?.map((product, index) => {
            const boxSize = isMobile ? '50' : (product?.field_value?.box_size?.field_value || '50');
            if (boxSize === '100') {
              return <Card100 product={product} key={index} index={index} info={info} isMobile={isMobile}/>;
            } else if (boxSize === '50' || boxSize === '33') {
              return <Card50 product={product} key={index} index={index} size={boxSize} info={info}
                             isMobile={isMobile}/>;
            }
            return null;
          })}
        </div>
      </Container>
      <Link
        className={classes.categoriesProductListCtaContainer}
        values={see_all_new_arrivals?.field_value}
        type={see_all_new_arrivals?.field_type}
        onClick={() =>
          GTMEvent(info, 'home_new-arrivals', 'click_link', see_all_new_arrivals?.field_value?.title)
        }
      >
        {see_all_new_arrivals?.field_value?.title}
      </Link>
    </div>
  );
};

CategoriesProductListManualV2Module.propTypes = {
  products: PropTypes.object,
  title: PropTypes.object,
  info: PropTypes.object,
  see_all_new_arrivals: {
    field_type: PropTypes.string,
    field_value: {
      href: PropTypes.string,
      title: PropTypes.string,
    },
  },
};

export default CategoriesProductListManualV2Module;